import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import loadable from "@loadable/component";
import { useConfig } from "@eis-poc/lib-discover-medical-mfes";
import {
  Skeleton,
  useMediaQuery,
  Flex
} from "discover.medical.react-component-library";
import {
  AssistantWrapper,
  FindOnPageBtnWrapper,
  ToolbarBackgroundAndBorder,
  ToolbarWrapper,
  ToolbarContainer
} from "./ToolbarFooter.styled";
import BackToTopBtn from "../BackToTopBtn";
import FeedbackButton from "../Feedback/components/FeedbackButton";
import SuccessCenterButton from "../SuccessCenterButton";
import CookieConsent from "../CookieConsent";
import { useDispatch } from "../../store";
import { resetFeedback } from "../../store/actions/feedback";
import { isOsanoAllowed } from "../../utils/authUtils";
import { useChurnZero } from "../../utils/churnZeroHelper";

const FeedbackWidget = loadable(() =>
  import("../Feedback/components/FeedbackWidget")
);
const Assistant = loadable(() => import("../Assistant"));

const getShouldShowCookieConsent = (userDetails, configData, productCode) =>
  !userDetails?.state?.organizational &&
  !userDetails?.state?.personalized &&
  !isOsanoAllowed(configData, productCode);

const ToolbarFooter = ({ productCode, feedbackUrl, eventBus }) => {
  const configData = useConfig();
  const dispatch = useDispatch();
  const [isFeedbackWidgetOpen, setIsFeedbackWidgetOpen] = useState(false);
  const [isCookieConsentOpen, setIsCookieConsentOpen] = useState(false);

  useEffect(() => {
    const userHasAcceptedCookies = !!document.cookie
      .split("; ")
      .find((item) => item.indexOf("gdpr-settings") === 0);
    setIsCookieConsentOpen(!userHasAcceptedCookies);
  }, []);

  const userDetails = configData.header?.userDetails;

  const shouldShowCookieConsent = getShouldShowCookieConsent(
    userDetails,
    configData,
    productCode
  );

  const assistant = configData.toolbarFooter?.assistant;
  const profileInfo = {
    customerId: userDetails?.affiliationId ?? "NA",
    userId: userDetails?.puaId ?? "NA",
    authenticationType:
      userDetails?.trafficSourceInfo?.authMethodPrimary ?? "unauthenticated"
  };

  const { hasSuccessCenter, openSuccessCenter } = useChurnZero(productCode);

  const isMobileButtons = useMediaQuery("max", "md");

  const handleAcceptCookies = () => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    document.cookie = `gdpr-settings=${encodeURI(
      '[{"tracker": "GDPR set"}]'
    )}; path=/; expires=${date.toUTCString()}; Secure; SameSite=None`;
    setIsCookieConsentOpen(false);
  };

  return (
    <ToolbarWrapper className="bui-fixed">
      {shouldShowCookieConsent && isCookieConsentOpen && (
        <CookieConsent
          acceptCookiesConsent={handleAcceptCookies}
          dismissCookiesConsent={() => setIsCookieConsentOpen(false)}
        />
      )}

      <ToolbarBackgroundAndBorder>
        <ToolbarContainer
          container
          justify={{ content: "space-between" }}
          align={{ item: "center" }}
        >
          <FindOnPageBtnWrapper id="toolbar-btn"></FindOnPageBtnWrapper>
          <Flex cell align={{ item: "center" }}>
            {assistant && !configData.shouldUseNewAssistant && (
              //TODO: assistant to be removed after new assistant is deployed
              <AssistantWrapper>
                <Assistant
                  isMobile={isMobileButtons}
                  assistant={assistant}
                  eventBus={eventBus}
                />
              </AssistantWrapper>
            )}
            {hasSuccessCenter && (
              <SuccessCenterButton
                isMobile={isMobileButtons}
                onClick={openSuccessCenter}
              />
            )}
            {!isFeedbackWidgetOpen && (
              <FeedbackButton
                isMobile={isMobileButtons}
                clickHandler={() => setIsFeedbackWidgetOpen(true)}
              />
            )}
            <BackToTopBtn />
          </Flex>

          {isFeedbackWidgetOpen && (
            <FeedbackWidget
              isWidgetOpen={isFeedbackWidgetOpen}
              handleWidgetClose={() => {
                setIsFeedbackWidgetOpen(false);
                resetFeedback(dispatch);
              }}
              feedbackUrl={feedbackUrl}
              productType={productCode.toUpperCase()}
              profileInfo={profileInfo}
              fallback={
                <Skeleton
                  kind="rect"
                  width={130}
                  height={40}
                  color="primary"
                  animate
                />
              }
            />
          )}
        </ToolbarContainer>
      </ToolbarBackgroundAndBorder>
    </ToolbarWrapper>
  );
};

ToolbarFooter.propTypes = {
  productCode: PropTypes.string.isRequired,
  feedbackUrl: PropTypes.string.isRequired,
  eventBus: PropTypes.object.isRequired
};

export default ToolbarFooter;
